import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Header } from "@/pages/Vitals";
import { useAuth } from "@/context/AuthContext";
import {
  Shield, Users, Crown, Search, CalendarClock, User as UserIcon, KeyRound,
  FileSpreadsheet, ShieldCheck, ShieldOff, AlertTriangle, ClipboardList,
  MessageSquareWarning, Trash2, TrendingUp, IndianRupee, Wallet, Percent,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { const d = new Date(iso); if (isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  } catch { return "—"; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function Admin() {
  return (
    <div className="space-y-6">
      <Header title="Admin"
        subtitle="DPDP-compliant overview. You can see account identity, subscription, consents and member count — never health information." />

      <Card className="p-3 rounded-xl border border-[#4A6750]/40 bg-[#4A6750]/5 flex items-start gap-3" data-testid="admin-dpdp-banner">
        <ShieldCheck className="w-4 h-4 text-[#4A6750] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-[#1C2E24]">
          <b>DPDP Act 2023:</b> Admin role has zero access to user health data (vitals, symptoms, diagnostics, consultations, medications). All admin reads are logged.
        </p>
      </Card>

      <Tabs defaultValue="users">
        <TabsList className="bg-[#F1F3F0]">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
          <TabsTrigger value="grievances" data-testid="tab-grievances">Grievances</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="revenue" className="mt-4"><RevenueTab /></TabsContent>
        <TabsContent value="grievances" className="mt-4"><GrievancesTab /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================== USERS TAB ============================== */
function UsersTab() {
  const { user: adminUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats")]);
    setUsers(u.data); setStats(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setTier = async (uid, tier) => {
    try {
      await api.patch(`/admin/users/${uid}/tier`, { tier });
      toast.success(`Updated to ${tier === "pro" ? "Paid (Pro)" : "Free"}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const deleteUser = async (u, force = false) => {
    try {
      await api.delete(`/admin/users/${u.id}${force ? "?force=true" : ""}`);
      toast.success(`Deleted ${u.name || "user"}`);
      load();
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail || "Delete failed";
      if (code === 409) {
        // Admin target — bubble up so the dialog can offer a force retry
        throw { detail, canForce: true };
      }
      toast.error(detail);
      throw { detail };
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await fetch(`${API}/admin/users.csv`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `verda-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("CSV downloaded");
    } catch { toast.error("Could not download CSV"); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return users.filter(u => {
      if (tierFilter === "free" && u.tier !== "free") return false;
      if (tierFilter === "pro" && u.tier !== "pro") return false;
      if (!s) return true;
      if ((u.name || "").toLowerCase().includes(s)) return true;
      if ((u.phone_masked || "").toLowerCase().includes(s)) return true;
      return false;
    });
  }, [users, q, tierFilter]);

  const toggleOne = useCallback((uid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }, []);

  const allFilteredSelected = filtered.length > 0 && filtered.every(u => selected.has(u.id));
  const toggleAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach(u => next.delete(u.id));
      } else {
        filtered.forEach(u => next.add(u.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async ({ password, force }) => {
    const ids = Array.from(selected);
    const r = await api.post("/admin/users/delete-bulk", { user_ids: ids, password, force });
    const del = r.data.deleted?.length || 0;
    const skip = r.data.skipped || [];
    if (del) toast.success(`Deleted ${del} user${del === 1 ? "" : "s"}`);
    if (skip.length) {
      const adminSkips = skip.filter(s => s.reason === "admin");
      if (adminSkips.length) {
        toast.message(`Skipped ${adminSkips.length} admin${adminSkips.length === 1 ? "" : "s"} — use force to include them`);
      } else if (skip.length) {
        toast.message(`Skipped ${skip.length} account${skip.length === 1 ? "" : "s"}`);
      }
    }
    clearSelection();
    load();
    return r.data;
  };

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat icon={Users} label="Total users" value={stats.total_users} color="#4A6750" />
          <Stat icon={Crown} label="Paid users" value={stats.pro_users} color="#C76B58" />
          <Stat icon={Shield} label="Free users" value={stats.free_users} color="#5B7582" />
          <Stat icon={Trash2} label="Pending deletions" value={stats.pending_deletions} color="#1C2E24" />
          <Stat icon={MessageSquareWarning} label="Open grievances" value={stats.grievances_open} color="#C76B58" />
        </div>
      )}

      <Card className="rounded-2xl border border-[#E5E7E4] overflow-hidden">
        <div className="p-4 border-b border-[#E5E7E4] flex flex-wrap items-center gap-3 bg-[#F9F8F6]">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-[#7D8F84]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or masked phone…"
              className="bg-white border-[#E5E7E4] rounded-xl max-w-md" data-testid="admin-search" />
          </div>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger data-testid="tier-filter" className="w-36 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              <SelectItem value="free">Free only</SelectItem>
              <SelectItem value="pro">Paid only</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={downloadCsv} variant="outline" data-testid="admin-csv-btn" className="rounded-xl border-[#E5E7E4]">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-[#4A6750]" /> CSV
          </Button>
          <span className="text-sm text-[#7D8F84] ml-auto">{filtered.length} of {users.length}</span>
        </div>

        {/* Selection strip — ALWAYS visible so the affordance is obvious */}
        <div className="px-4 sm:px-6 py-2.5 border-b border-[#E5E7E4] flex items-center gap-3 bg-white sticky top-0 z-10"
             data-testid="selection-strip">
          <label className="flex items-center gap-2 cursor-pointer select-none hover:bg-[#F1F3F0] px-2 py-1 -mx-2 rounded-lg transition-colors">
            <Checkbox
              checked={filtered.length > 0 && allFilteredSelected}
              onCheckedChange={toggleAllFiltered}
              data-testid="header-select-all"
              className="w-5 h-5 border-2 border-[#4A6750] data-[state=checked]:bg-[#C76B58] data-[state=checked]:border-[#C76B58]"
            />
            <span className="text-xs uppercase tracking-wider text-[#5C6D63] font-semibold">
              Select all in view
            </span>
          </label>
          <span className="text-xs text-[#7D8F84]">Tap any row to select</span>
          {selected.size > 0 && (
            <button onClick={clearSelection} data-testid="bulk-clear-inline"
                    className="text-xs text-[#5C6D63] hover:text-[#1C2E24] underline ml-auto">
              Clear selection
            </button>
          )}
        </div>

        {/* Sticky bulk action bar — appears with a strong red band the moment any row is picked */}
        {selected.size > 0 && (
          <div className="px-4 sm:px-6 py-3 border-b-2 border-[#C76B58] bg-gradient-to-r from-[#C76B58] to-[#A85546] text-white flex flex-wrap items-center gap-3 sticky top-[42px] z-10 animate-in fade-in slide-in-from-top-2 duration-200"
               data-testid="bulk-action-bar">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm" data-testid="bulk-selected-count">
                {selected.size} account{selected.size === 1 ? "" : "s"} selected
              </p>
              <p className="text-[11px] text-white/80">
                Bulk delete requires re-entering your admin password
              </p>
            </div>
            <Button onClick={clearSelection} variant="ghost" data-testid="bulk-clear"
                    className="h-9 rounded-xl text-white hover:bg-white/20 hover:text-white">
              Cancel
            </Button>
            <Button onClick={() => setBulkOpen(true)} data-testid="bulk-delete-btn"
                    className="h-9 rounded-xl bg-white text-[#C76B58] hover:bg-[#F9F8F6] font-semibold">
              <Trash2 className="w-4 h-4 mr-2" /> Delete {selected.size}
            </Button>
          </div>
        )}

        <ul className="divide-y divide-[#E5E7E4]">
          {filtered.length === 0 && (
            <li className="px-6 py-10 text-center text-sm text-[#7D8F84] italic">No users match your filters.</li>
          )}
          {filtered.map(u => (
            <UserRow key={u.id} user={u} onTier={setTier} onDelete={deleteUser}
                     selected={selected.has(u.id)} onToggle={() => toggleOne(u.id)} />
          ))}
        </ul>
      </Card>

      <BulkDeleteDialog open={bulkOpen} onOpenChange={setBulkOpen}
                        users={users.filter(u => selected.has(u.id))}
                        adminUser={adminUser} onSubmit={bulkDelete} />
    </div>
  );
}

function UserRow({ user, onTier, onDelete, selected, onToggle }) {
  const initials = (user.name || "·").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const isPro = user.tier === "pro";

  // Whole-row click toggles selection, EXCEPT when the click originated in an
  // interactive control (button, select, dialog, link, input).
  const rowClick = (e) => {
    if (e.target.closest("button, [role='combobox'], [role='dialog'], a, input, label, [data-no-select]")) return;
    onToggle();
  };

  return (
    <li className={`transition-colors ${selected ? "bg-[#C76B58]/10 border-l-4 border-l-[#C76B58]" : "bg-white border-l-4 border-l-transparent"}`}
        data-testid={`admin-user-${user.id}`}
        data-selected={selected}>
      <div onClick={rowClick}
           className={`px-4 sm:px-6 py-4 flex items-start gap-3 cursor-pointer ${selected ? "" : "hover:bg-[#F9F8F6]"}`}>
        <label className="flex-shrink-0 mt-1 p-1 -m-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={onToggle}
            data-testid={`select-user-${user.id}`}
            className="w-5 h-5 border-2 border-[#4A6750] data-[state=checked]:bg-[#C76B58] data-[state=checked]:border-[#C76B58] data-[state=checked]:text-white"
          />
        </label>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${selected ? "bg-[#C76B58]/20 text-[#C76B58]" : "bg-[#E8ECE9] text-[#4A6750]"}`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="font-medium text-[#1C2E24] truncate">{user.name}</p>
            {user.is_admin && <span className="text-[10px] uppercase tracking-wider bg-[#1C2E24] text-white px-1.5 py-0.5 rounded">Admin</span>}
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${isPro ? "bg-[#C76B58]/10 text-[#C76B58]" : "bg-[#E8ECE9] text-[#4A6750]"}`}>
              {isPro ? <Crown className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
              {isPro ? "Paid" : "Free"}
            </span>
            {user.mfa_enabled && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] bg-[#1C2E24]/10 text-[#1C2E24]" title="2FA enabled">
                <KeyRound className="w-3 h-3" /> 2FA
              </span>
            )}
            {user.pending_deletion && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] bg-[#C76B58]/15 text-[#C76B58]">
                <AlertTriangle className="w-3 h-3" /> Pending deletion
              </span>
            )}
          </div>
          <p className="text-sm text-[#5C6D63] font-mono mt-0.5 truncate">{user.phone_masked}</p>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5C6D63]">
            <span className="flex items-center gap-1">
              <CalendarClock className="w-3 h-3" />
              Since <b className="text-[#1C2E24]">{fmtDate(user.created_at)}</b>
            </span>
            <span>·</span>
            <span><UserIcon className="w-3 h-3 inline mr-1" /><b className="text-[#1C2E24]">{user.member_count}</b> profile{user.member_count === 1 ? "" : "s"}</span>
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {(user.granted_consents || []).map(c => (
              <span key={c} className="text-[10px] inline-flex items-center gap-1 bg-[#5B8266]/10 text-[#5B8266] px-1.5 py-0.5 rounded">
                <ShieldCheck className="w-2.5 h-2.5" /> {c}
              </span>
            ))}
            {(user.withdrawn_consents || []).map(c => (
              <span key={c} className="text-[10px] inline-flex items-center gap-1 bg-[#C76B58]/10 text-[#C76B58] px-1.5 py-0.5 rounded">
                <ShieldOff className="w-2.5 h-2.5" /> {c}
              </span>
            ))}
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col gap-1.5">
          <Select value={user.tier || "free"} onValueChange={(v) => onTier(user.id, v)}>
            <SelectTrigger data-testid={`tier-select-${user.id}`} className="h-9 rounded-lg w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="pro">Paid (Pro)</SelectItem>
            </SelectContent>
          </Select>
          <InsightsButton uid={user.id} name={user.name}
                          hasConsent={(user.granted_consents || []).includes("admin_health_review")} />
          <SymptomsButton uid={user.id} name={user.name}
                          hasConsent={(user.granted_consents || []).includes("admin_health_review")} />
          <DeleteUserButton user={user} onDelete={onDelete} />
        </div>
      </div>
    </li>
  );
}

function BulkDeleteDialog({ open, onOpenChange, users, adminUser, onSubmit }) {
  const [password, setPassword] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setPassword(""); setForce(false); } }, [open]);

  const adminTargets = users.filter(u => u.is_admin);
  const adminIdentity = adminUser?.email || adminUser?.name || "admin";
  const submit = async () => {
    if (!password) { toast.error("Enter your admin password"); return; }
    if (users.length === 0) { onOpenChange(false); return; }
    setBusy(true);
    try {
      await onSubmit({ password, force });
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Bulk delete failed";
      const code = err?.response?.status;
      toast.error(detail);
      if (code === 403) setPassword("");  // password mismatch — keep dialog open for retry
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete {users.length} account{users.length === 1 ? "" : "s"}?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6D63]">
            You&apos;re about to <b>permanently remove</b> {users.length} account{users.length === 1 ? "" : "s"} and every record attached to them (vitals, symptoms, diagnostics, consultations, medications, adherence, family profiles, payment history, consents). This cannot be undone.
          </p>

          <div className="max-h-32 overflow-y-auto rounded-xl bg-[#F9F8F6] border border-[#E5E7E4] p-2"
               data-testid="bulk-delete-preview">
            {users.slice(0, 30).map(u => (
              <div key={u.id} className="flex items-center justify-between text-xs py-1 px-1.5">
                <span className="text-[#1C2E24] truncate">{u.name || "—"}</span>
                <span className="flex items-center gap-1 text-[10px] text-[#7D8F84]">
                  {u.is_admin && <span className="uppercase tracking-wider bg-[#1C2E24] text-white px-1 py-0.5 rounded text-[9px]">Admin</span>}
                  {u.tier === "pro" ? "Pro" : "Free"}
                </span>
              </div>
            ))}
            {users.length > 30 && (
              <p className="text-[10px] text-[#7D8F84] italic mt-1 pl-1.5">…and {users.length - 30} more</p>
            )}
          </div>

          {adminTargets.length > 0 && (
            <div className="p-3 rounded-xl bg-[#C76B58]/10 border border-[#C76B58]/40 text-xs text-[#A85546] flex items-start gap-2"
                 data-testid="bulk-admin-warning">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p><b>{adminTargets.length} admin account{adminTargets.length === 1 ? "" : "s"}</b> selected. They&apos;ll be skipped unless you enable force.</p>
                <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="bulk-force-label">
                  <Checkbox checked={force} onCheckedChange={(v) => setForce(!!v)}
                            data-testid="bulk-force-toggle" />
                  <span>Also delete admin accounts (force)</span>
                </label>
              </div>
            </div>
          )}

          <div className="p-3 rounded-xl bg-[#4A6750]/8 border border-[#4A6750]/25">
            <p className="text-[11px] uppercase tracking-wider text-[#4A6750] font-semibold mb-2">
              Signed in as
            </p>
            <p className="text-sm font-medium text-[#1C2E24] break-all" data-testid="bulk-admin-identity">
              {adminIdentity}
            </p>
            <div className="mt-3">
              <Label htmlFor="bulk-pw" className="text-xs">
                Enter <b>your</b> admin password to confirm
              </Label>
              <Input id="bulk-pw" type="password" value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     placeholder="Your password"
                     data-testid="bulk-password-input" autoFocus
                     autoComplete="current-password"
                     onKeyDown={(e) => { if (e.key === "Enter" && !busy) submit(); }}
                     className="mt-2 h-10 rounded-xl bg-white" />
              <p className="text-[10px] text-[#7D8F84] mt-1">
                Same password you use at /admin/login.
              </p>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="bulk-cancel"
                    className="flex-1 h-10 rounded-xl border-[#E5E7E4]">
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy}
                    data-testid="bulk-confirm"
                    className="flex-1 h-10 rounded-xl bg-[#C76B58] hover:bg-[#A85546]">
              <Trash2 className="w-4 h-4 mr-2" />
              {busy ? "Deleting…" : `Delete ${users.length}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function DeleteUserButton({ user, onDelete }) {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsForce, setNeedsForce] = useState(false);

  const expected = `delete ${(user.name || "").trim()}`.toLowerCase();

  const submit = async () => {
    if (phrase.trim().toLowerCase() !== expected) {
      toast.error(`Type "delete ${user.name}" to confirm`);
      return;
    }
    setBusy(true);
    try {
      await onDelete(user, needsForce);
      setOpen(false); setPhrase(""); setNeedsForce(false);
    } catch (err) {
      if (err?.canForce) setNeedsForce(true);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
              data-testid={`delete-btn-${user.id}`}
              className="h-8 rounded-lg text-xs border-[#C76B58]/50 text-[#C76B58] hover:bg-[#C76B58]/10">
        <Trash2 className="w-3 h-3 mr-1" /> Delete
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setPhrase(""); setNeedsForce(false); } }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Delete {user.name}?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#5C6D63]">
              This <b>permanently removes</b> the account and all associated data:
              vitals, symptoms, diagnostics, consultations, medications, adherence,
              family profiles, files, payment history and consent records. This
              cannot be undone.
            </p>
            {user.is_admin && (
              <div className="p-3 rounded-xl bg-[#C76B58]/10 border border-[#C76B58]/40 text-xs text-[#A85546]">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This is an <b>admin account</b>. Deleting it also frees its email seat.
              </div>
            )}
            {needsForce && !user.is_admin && (
              <div className="p-3 rounded-xl bg-[#C76B58]/10 border border-[#C76B58]/40 text-xs text-[#A85546]">
                Server flagged this account as admin. Confirm again to force-delete.
              </div>
            )}
            <div>
              <Label htmlFor={`dc-${user.id}`} className="text-xs">
                Type <b className="text-[#1C2E24]">delete {user.name}</b> to confirm
              </Label>
              <Input id={`dc-${user.id}`} value={phrase} onChange={(e) => setPhrase(e.target.value)}
                     data-testid={`delete-confirm-input-${user.id}`} autoFocus
                     className="mt-2 h-10 rounded-xl bg-[#F9F8F6]" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setOpen(false)}
                      data-testid={`delete-cancel-${user.id}`}
                      className="flex-1 h-10 rounded-xl border-[#E5E7E4]">
                Cancel
              </Button>
              <Button onClick={submit} disabled={busy}
                      data-testid={`delete-confirm-${user.id}`}
                      className="flex-1 h-10 rounded-xl bg-[#C76B58] hover:bg-[#A85546]">
                <Trash2 className="w-4 h-4 mr-2" />
                {busy ? "Deleting…" : (needsForce || user.is_admin ? "Force delete" : "Delete permanently")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SymptomsButton({ uid, name, hasConsent }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null); setData(null);
    try {
      const r = await api.get(`/admin/users/${uid}/symptoms?days=90`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load symptoms");
    } finally { setLoading(false); }
  };

  const openDlg = () => { setOpen(true); load(); };

  return (
    <>
      <Button size="sm" variant="outline" onClick={openDlg}
              data-testid={`symptoms-btn-${uid}`}
              className={`h-8 rounded-lg text-xs ${hasConsent ? "border-[#C76B58] text-[#C76B58]" : "border-[#E5E7E4] text-[#7D8F84]"}`}>
        Symptoms {hasConsent ? "" : "🔒"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-2xl" data-testid="symptoms-dialog">
          <DialogHeader><DialogTitle>Symptom logs — {name}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#7D8F84]">Last 90 days · consent-gated · every access is audit-logged.</p>
          {loading && <p className="text-sm text-[#7D8F84]">Loading…</p>}
          {error && (
            <div className="p-4 rounded-xl bg-[#C76B58]/10 border border-[#C76B58]/40 text-sm text-[#A85546]">
              {error}
              <p className="text-xs text-[#5C6D63] mt-2">The user must enable &quot;Allow admin health review&quot; in their Privacy &amp; Data settings before you can view symptom logs.</p>
            </div>
          )}
          {data && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="symptoms-list">
              {data.count === 0 && <p className="text-sm text-[#7D8F84]">No symptoms logged in the last {data.days} days.</p>}
              {data.symptoms.map((s) => (
                <div key={s.id} className="p-3 rounded-xl border border-[#E5E7E4] bg-[#F9F8F6]" data-testid={`symptom-row-${s.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-heading text-sm font-semibold text-[#1C2E24]">{s.name || "(untitled symptom)"}</p>
                    <span className="text-[10px] uppercase tracking-wider text-[#7D8F84]">{fmtDate(s.date)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1.5 text-[11px] text-[#5C6D63]">
                    {s.severity && <span className="px-1.5 py-0.5 rounded bg-white border border-[#E5E7E4]">Severity: {s.severity}</span>}
                    {s.duration && <span className="px-1.5 py-0.5 rounded bg-white border border-[#E5E7E4]">Duration: {s.duration}</span>}
                    {s.body_part && <span className="px-1.5 py-0.5 rounded bg-white border border-[#E5E7E4]">Body: {s.body_part}</span>}
                  </div>
                  {s.notes && <p className="text-xs text-[#1C2E24] mt-2 whitespace-pre-wrap">{s.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function InsightsButton({ uid, name, hasConsent }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null); setData(null);
    try {
      const r = await api.get(`/admin/users/${uid}/insights`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load insights");
    } finally { setLoading(false); }
  };

  const openDlg = () => { setOpen(true); load(); };

  return (
    <>
      <Button size="sm" variant="outline" onClick={openDlg}
              data-testid={`insights-btn-${uid}`}
              className={`h-8 rounded-lg text-xs ${hasConsent ? "border-[#5B8266] text-[#4A6750]" : "border-[#E5E7E4] text-[#7D8F84]"}`}>
        Insights {hasConsent ? "" : "🔒"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="insights-dialog">
          <DialogHeader><DialogTitle>Health insights — {name}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#7D8F84]">30-day aggregates only. Raw notes never shown. Access is audit-logged.</p>
          {loading && <p className="text-sm text-[#7D8F84]">Loading…</p>}
          {error && (
            <div className="p-4 rounded-xl bg-[#C76B58]/10 border border-[#C76B58]/40 text-sm text-[#A85546]">
              {error}
              <p className="text-xs text-[#5C6D63] mt-2">The user must enable "Allow admin health review" in their Privacy &amp; Data settings before you can view insights.</p>
            </div>
          )}
          {data && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#7D8F84] mb-1.5">Activity (last 30 days)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(data.counts).map(([k, v]) => (
                    <div key={k} className="p-2.5 rounded-lg bg-[#F9F8F6] border border-[#E5E7E4]">
                      <p className="text-[10px] text-[#7D8F84] uppercase tracking-wider">{k.replace(/_/g, " ")}</p>
                      <p className="font-heading text-lg font-semibold">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#7D8F84] mb-1.5">Vitals aggregates (30d)</p>
                <table className="w-full text-sm">
                  <thead className="text-[10px] text-[#7D8F84] uppercase tracking-wider">
                    <tr><th className="text-left py-1">Metric</th><th>Count</th><th>Min</th><th>Avg</th><th>Max</th></tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7E4]">
                    {Object.entries(data.aggregates_30d).map(([k, agg]) => (
                      <tr key={k}>
                        <td className="py-1.5 text-[#1C2E24] font-medium">{k.replace(/_/g, " ")}</td>
                        <td className="text-center text-[#5C6D63]">{agg ? agg.count : "—"}</td>
                        <td className="text-center text-[#5C6D63]">{agg ? agg.min : "—"}</td>
                        <td className="text-center font-medium text-[#1C2E24]">{agg ? agg.avg : "—"}</td>
                        <td className="text-center text-[#5C6D63]">{agg ? agg.max : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-[#7D8F84] italic">Generated at {new Date(data.generated_at).toLocaleString()}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ============================== GRIEVANCES TAB ============================== */
function GrievancesTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState("resolved");

  const load = useCallback(async () => {
    const r = await api.get("/admin/grievances");
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(g => filter === "all" || g.status === filter);

  const respond = async () => {
    if (!response.trim()) { toast.error("Enter a response"); return; }
    try {
      await api.patch(`/admin/grievances/${editing.id}`, { response, status });
      toast.success("Response sent");
      setEditing(null); setResponse(""); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger data-testid="grievance-filter" className="w-44 rounded-xl bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All grievances</SelectItem>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="in_progress">In progress</SelectItem>
          <SelectItem value="resolved">Resolved</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
        </SelectContent>
      </Select>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-[#7D8F84] italic rounded-2xl border-[#E5E7E4]">No grievances.</Card>
      ) : (
        <ul className="space-y-2">
          {filtered.map(g => (
            <Card key={g.id} className="p-4 rounded-xl border-[#E5E7E4]" data-testid={`grievance-row-${g.id}`}>
              <div className="flex items-start gap-3">
                <MessageSquareWarning className="w-4 h-4 text-[#C76B58] flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{g.subject}</p>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E8ECE9] text-[#4A6750]">{g.status}</span>
                    <span className="text-xs text-[#7D8F84]">· {g.category}</span>
                  </div>
                  <p className="text-xs text-[#7D8F84] mt-1">{g.user_name} · {g.user_phone} · {fmtDateTime(g.created_at)}</p>
                  <p className="text-sm text-[#5C6D63] mt-2">{g.message}</p>
                  {g.response && (
                    <div className="mt-2 p-2.5 rounded-lg bg-[#F9F8F6] text-sm">
                      <p className="text-[10px] font-semibold uppercase text-[#4A6750]">Response · {fmtDateTime(g.resolved_at)}</p>
                      <p className="mt-1">{g.response}</p>
                    </div>
                  )}
                </div>
                {g.status !== "resolved" && (
                  <Button size="sm" variant="outline" data-testid={`respond-${g.id}`}
                          onClick={() => { setEditing(g); setResponse(""); setStatus("resolved"); }}
                          className="rounded-lg">Respond</Button>
                )}
              </div>
            </Card>
          ))}
        </ul>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>Respond to grievance</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="p-3 rounded-xl bg-[#F9F8F6] text-sm">
                <p className="font-medium">{editing.subject}</p>
                <p className="text-xs text-[#5C6D63] mt-1">{editing.message}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-[#7D8F84]">Status</p>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1.5 rounded-xl bg-[#F9F8F6]" data-testid="respond-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea rows={4} value={response} onChange={(e) => setResponse(e.target.value)}
                        placeholder="Your response to the user…" data-testid="respond-text"
                        className="rounded-xl bg-[#F9F8F6]" />
              <Button onClick={respond} data-testid="respond-submit"
                      className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">Send response</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================== AUDIT TAB ============================== */
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { (async () => {
    const r = await api.get("/admin/audit-logs?limit=300");
    setLogs(r.data);
  })(); }, []);

  const filtered = logs.filter(l => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (l.action || "").toLowerCase().includes(s)
      || (l.actor_role || "").toLowerCase().includes(s)
      || JSON.stringify(l.meta || {}).toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-[#7D8F84]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search"
               placeholder="Filter by action, role…" className="max-w-md rounded-xl bg-white" />
        <span className="text-xs text-[#7D8F84] ml-auto">{filtered.length} entries</span>
      </div>
      <Card className="rounded-2xl border-[#E5E7E4] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-wider text-[#7D8F84]">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Target</th>
              <th className="text-left px-4 py-3">Meta</th>
              <th className="text-left px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7E4]">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#7D8F84] italic">No audit log entries.</td></tr>
            )}
            {filtered.map(l => (
              <tr key={l.id} className="hover:bg-[#F9F8F6]/60" data-testid={`audit-row-${l.id}`}>
                <td className="px-4 py-2.5 text-xs text-[#5C6D63] whitespace-nowrap font-mono">{fmtDateTime(l.at)}</td>
                <td className="px-4 py-2.5 text-xs">
                  <span className={`uppercase tracking-wider px-1.5 py-0.5 rounded text-[10px] ${l.actor_role === "admin" ? "bg-[#1C2E24] text-white" : "bg-[#E8ECE9] text-[#4A6750]"}`}>
                    {l.actor_role}
                  </span>
                  <span className="font-mono text-[10px] text-[#7D8F84] ml-2">{(l.actor_id || "").slice(0, 8)}</span>
                </td>
                <td className="px-4 py-2.5 font-medium text-[#1C2E24]">{l.action}</td>
                <td className="px-4 py-2.5 text-xs text-[#5C6D63] font-mono">{(l.target_user_id || l.target_resource || "—").slice(0, 12)}</td>
                <td className="px-4 py-2.5 text-xs text-[#5C6D63] font-mono max-w-xs truncate">{Object.keys(l.meta || {}).length ? JSON.stringify(l.meta) : "—"}</td>
                <td className="px-4 py-2.5 text-xs text-[#7D8F84] font-mono">{l.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ============================== SHARED ============================== */
function Stat({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 rounded-2xl border border-[#E5E7E4]" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color }} />
        </div>
        <div>
          <p className="text-[10px] font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">{label}</p>
          <p className="font-heading text-xl font-semibold leading-tight">{value}</p>
        </div>
      </div>
    </Card>
  );
}

/* ============================== REVENUE TAB ============================== */
function RevenueTab() {
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [txns, setTxns] = useState([]);
  const [months, setMonths] = useState(6);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, x] = await Promise.all([
        api.get("/admin/revenue/summary"),
        api.get(`/admin/revenue/timeseries?months=${months}`),
        api.get(`/admin/revenue/transactions?limit=25&status=${statusFilter}`),
      ]);
      setSummary(s.data); setSeries(t.data.series || []); setTxns(x.data.transactions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load revenue");
    } finally { setLoading(false); }
  }, [months, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const cur = summary?.currency || "INR";
  const fmtMoney = useCallback((n) => {
    if (n == null || isNaN(n)) return "—";
    try {
      return new Intl.NumberFormat("en-IN", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n);
    } catch { return `${cur} ${n}`; }
  }, [cur]);

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Total revenue", value: fmtMoney(summary.total_revenue), icon: IndianRupee, color: "#4A6750" },
      { label: "Monthly recurring", value: fmtMoney(summary.mrr), icon: TrendingUp, color: "#C76B58",
        hint: `${summary.pro_users} × ${fmtMoney(summary.plan_price)}` },
      { label: "Revenue · 30d", value: fmtMoney(summary.revenue_30d), icon: Wallet, color: "#5B7582",
        hint: `${summary.new_pro_30d} new Pro` },
      { label: "Conversion", value: `${summary.conversion_pct}%`, icon: Percent, color: "#7A5B82",
        hint: `${summary.pro_users}/${summary.pro_users + summary.free_users} paid` },
      { label: "ARPPU", value: fmtMoney(summary.arppu), icon: Crown, color: "#B8873D",
        hint: `Avg ticket ${fmtMoney(summary.avg_ticket)}` },
    ];
  }, [summary, fmtMoney]);

  return (
    <div className="space-y-6" data-testid="revenue-tab">
      {loading && <p className="text-sm text-[#7D8F84]">Loading revenue…</p>}

      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="revenue-kpis">
            {kpis.map(k => (
              <Card key={k.label} className="p-4 rounded-2xl border border-[#E5E7E4]">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${k.color}15` }}>
                    <k.icon className="w-4 h-4" strokeWidth={1.5} style={{ color: k.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">{k.label}</p>
                    <p className="font-heading text-xl font-semibold leading-tight truncate">{k.value}</p>
                    {k.hint && <p className="text-[10px] text-[#7D8F84] mt-0.5 truncate">{k.hint}</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <Card className="p-5 rounded-2xl border border-[#E5E7E4]" data-testid="revenue-chart-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading text-lg font-semibold text-[#1C2E24]">Revenue trend</h3>
                <p className="text-xs text-[#7D8F84]">Monthly earned revenue · currency {cur}</p>
              </div>
              <div className="flex items-center gap-1 bg-[#F1F3F0] p-0.5 rounded-xl">
                {[3, 6, 12].map(m => (
                  <button key={m} onClick={() => setMonths(m)}
                          data-testid={`revenue-range-${m}m`}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${months === m ? "bg-white text-[#1C2E24] shadow-sm" : "text-[#5C6D63]"}`}>
                    {m}m
                  </button>
                ))}
              </div>
            </div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revfill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4A6750" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#4A6750" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E5E7E4" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#7D8F84" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#7D8F84" }} tickLine={false} axisLine={false}
                         tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v) => fmtMoney(v)} labelStyle={{ color: "#1C2E24", fontWeight: 600 }}
                           contentStyle={{ background: "#fff", border: "1px solid #E5E7E4", borderRadius: 12 }} />
                  <Area type="monotone" dataKey="revenue" stroke="#4A6750" strokeWidth={2} fill="url(#revfill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-[11px] text-[#5C6D63]">
              <SubStat label="Paying users" value={summary.paying_users} />
              <SubStat label="Paid txns" value={summary.paid_txn_count} />
              <SubStat label="Pending txns" value={summary.pending_txn_count} />
              <SubStat label="ARR (est.)" value={fmtMoney(summary.arr)} />
              <SubStat label="Free users" value={summary.free_users} />
              <SubStat label="Pro users" value={summary.pro_users} />
            </div>
          </Card>
        </>
      )}

      <Card className="p-5 rounded-2xl border border-[#E5E7E4]" data-testid="revenue-txns-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading text-lg font-semibold text-[#1C2E24]">Recent transactions</h3>
            <p className="text-xs text-[#7D8F84]">Latest {txns.length} · consent-safe (no health data)</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="revenue-status-filter" className="h-9 rounded-lg w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {txns.length === 0 ? (
          <p className="text-sm text-[#7D8F84] py-8 text-center">No transactions.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="revenue-txns-table">
              <thead className="text-[10px] text-[#7D8F84] uppercase tracking-wider border-b border-[#E5E7E4]">
                <tr>
                  <th className="text-left py-2 font-semibold">Date</th>
                  <th className="text-left py-2 font-semibold">User</th>
                  <th className="text-left py-2 font-semibold">Plan</th>
                  <th className="text-right py-2 font-semibold">Amount</th>
                  <th className="text-left py-2 font-semibold pl-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7E4]">
                {txns.map(t => (
                  <tr key={t.session_id} data-testid={`revenue-txn-${t.session_id}`}>
                    <td className="py-2 text-[#5C6D63] whitespace-nowrap">{fmtDateTime(t.applied_at || t.created_at)}</td>
                    <td className="py-2 text-[#1C2E24] font-medium truncate max-w-[180px]">{t.user_name}</td>
                    <td className="py-2 text-[#5C6D63]">{t.package_id || "—"}</td>
                    <td className="py-2 text-right font-medium text-[#1C2E24]">{fmtMoney(t.amount)}</td>
                    <td className="py-2 pl-3"><TxnBadge status={t.payment_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SubStat({ label, value }) {
  return (
    <div className="p-2 rounded-lg bg-[#F9F8F6] border border-[#E5E7E4]">
      <p className="text-[10px] uppercase tracking-wider text-[#7D8F84]">{label}</p>
      <p className="font-heading text-sm font-semibold text-[#1C2E24] mt-0.5">{value}</p>
    </div>
  );
}

function TxnBadge({ status }) {
  const map = {
    paid:    { color: "#5B8266", bg: "#5B8266/15", label: "Paid" },
    pending: { color: "#B8873D", bg: "#B8873D/15", label: "Pending" },
    expired: { color: "#7D8F84", bg: "#7D8F84/15", label: "Expired" },
    failed:  { color: "#C76B58", bg: "#C76B58/15", label: "Failed" },
  };
  const m = map[status] || { color: "#5C6D63", bg: "#5C6D63/15", label: status || "—" };
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ color: m.color, background: `${m.color}1A` }}>
      {m.label}
    </span>
  );
}

